import React, { type PointerEvent, type FocusEvent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { N90 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { BarChart as Bar } from '@atlassian/viz-platform-charts--next/charts';
import { element, useElementProps } from '@atlassian/viz-platform-charts--next/core';
import { ColorSchemes, type ColorScheme } from '../../types.tsx';

export type BarChartDatum = {
	name: string;
	count: number;
	jqlTerm?: string;
};

// `children` is typed as any in viz-platform-charts library
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type axisBottomTextTransformer = (children: any) => JSX.Element;

type BarChartProps = {
	data: BarChartDatum[];
	axisBottomTextTransformer?: axisBottomTextTransformer;
	onTooltipClick?: (params: { event?: PointerEvent | FocusEvent; datum: BarChartDatum }) => void;
	colorScheme?: ColorScheme;
	children?: JSX.Element;
};

const TextContainer = ({
	width,
	transform,
	children,
}: {
	width: number | undefined;
	transform: string | undefined;
	children: JSX.Element;
	// eslint-disable-next-line jira/react/no-style-attribute
}) => <div style={{ width, transform }}>{children}</div>;

const CustomAxisBottomText = element(
	({ transformer }: { transformer: axisBottomTextTransformer }) => {
		const { children, width, transform } = useElementProps(Bar.elements.AxisBottomText);

		return (
			<TextContainer width={width} transform={transform}>
				{transformer(children)}
			</TextContainer>
		);
	},
	{ as: Bar.elements.AxisBottomText },
);

const CustomLayout = element(
	() => {
		const { children } = useElementProps(Bar.elements.Layout);

		return (
			<Box data-element="layout" xcss={barLayoutStyles}>
				<Box data-element="chart" xcss={barChartStyles}>
					{children}
				</Box>
			</Box>
		);
	},
	{ as: Bar.elements.Layout },
);

export const BarChart = ({
	data,
	axisBottomTextTransformer: xAxisLabelTransformer = (children) => children,
	onTooltipClick,
	children,
	colorScheme = ColorSchemes.DEFAULT,
}: BarChartProps) => {
	return (
		<Bar data={data} xAccessor={(d) => d.name} yAccessor={(d) => d.count}>
			<Bar.elements.Theme
				fillStyles={
					colorScheme === ColorSchemes.BLUE
						? [token('color.chart.brand', '#1D7AFC')]
						: [token('color.chart.neutral', N90)]
				}
			/>
			<Bar.elements.TooltipEmitter onClick={onTooltipClick} />
			<CustomAxisBottomText transformer={xAxisLabelTransformer} />
			<CustomLayout />
			{children}
		</Bar>
	);
};

const barLayoutStyles = xcss({
	flexGrow: '1',
});

const barChartStyles = xcss({
	height: '100%',
	display: 'flex',
});
